import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import {
  ContainerForm,
  AInput,
  ASwitch,
  AInputSearch,
  ASelect,
  OptionFooter,
  OptionsClient,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { TeamOutlined } from '@ant-design/icons';
import { IClient, ICovenant } from '../../../shared/interfaces';
import { Col, Form, Row, Typography } from 'antd';
import {
  mask,
  cellPhoneMask,
  phoneMask,
} from '../../../shared/utils/functions';
import { schemaClient } from '../schemas/yup.client.schema';
import axios from 'axios';
import { MARITAL_STATUS_LIST } from '../../../shared/enums/marital_status.enum';
import { useConfiguration } from '../../../hooks/configuration';
import {
  getClient,
  getSelectCovenants,
  patchClients,
  postClient,
} from '../../../http';

const { Title } = Typography;

const ClientCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { configuration } = useConfiguration();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [client_code, setClient_code] = useState<number | null>(null);
  const [corporate_name, setCorporate_name] = useState<string>();
  const [fantasy_name, setFantasy_name] = useState<string>();
  const [cpf_cnpj, setCpf_cnpj] = useState<string>('');
  const [birthday, setBirthday] = useState<string>();
  const [home_phone, setHome_phone] = useState<string>();
  const [office_phone, setOffice_phone] = useState<string>();
  const [cell_phone, setCell_phone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);
  const [is_supplier, setIs_supplier] = useState<boolean>(false);
  const [showResponsable, setShowResponsable] = useState<boolean>(false);
  const [covenant_id, setCovenant_id] = useState<string>();
  const [covenant_card_number, setCovenant_card_number] = useState<string>();
  const [gender, setGender] = useState<string>();
  const [naturalness, setNaturalness] = useState<string>();
  const [nationality, setNationality] = useState<string>();
  const [marital_status, setMarital_status] = useState<number>();
  const [profession, setProfession] = useState<string>();
  const [responsible, setResponsible] = useState<string>();
  const [cpf_responsible, setCpf_responsible] = useState<string>();
  const [rg_responsible, setRg_responsible] = useState<string>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [clientObj, setClientObj] = useState<IClient | null>(null);

  const [covenants, setCovenants] = useState<ICovenant[]>([]);

  const loadData = useCallback(async () => {
    const data = await getClient(id);
    setClientObj(data);
    setCorporate_name(data.corporate_name);
    setFantasy_name(data.fantasy_name);
    setCpf_cnpj(data.cpf_cnpj);
    setBirthday(data.birthday);
    setHome_phone(data.home_phone);
    setOffice_phone(data.office_phone);
    setCell_phone(data.cell_phone);
    setEmail(data.email);
    setStatus(data.status);
    setIs_supplier(data.is_supplier);
    setCovenant_card_number(data.covernant);
    setGender(data.gender);
    setNaturalness(data.naturalness);
    setNationality(data.nationality);
    setMarital_status(data.marital_status);
    setProfession(data.profession);
    setResponsible(data.responsible);
    setCpf_responsible(data.cpf_responsible);
    setRg_responsible(data.rg_responsible);
    setClient_code(data.client_code ?? null);
  }, []);

  const listCovenants = useCallback(async () => {
    await getSelectCovenants()
      .then((data) => setCovenants(data))
      .catch(() => {
        void 0;
      });
  }, []);

  const handleSearchCnpj = useCallback(async () => {
    const CNPJ = cpf_cnpj?.replace(/[^0-9]/g, '');
    await axios
      .get(`${process.env.REACT_APP_BRASIL_API}cnpj/v1/${CNPJ}`)
      .then((response) => {
        setCorporate_name(response.data.razao_social);
        setFantasy_name(response.data.nome_fantasia);
        setBirthday(response.data.data_inicio_atividade);
        setHome_phone(phoneMask(response.data.ddd_telefone_1));
        setOffice_phone(phoneMask(response.data.ddd_telefone_2));
        setEmail(response.data.email);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `Erro ao pesquisar CNPJ`,
        });
      });
  }, [cpf_cnpj]);

  const handleEditSubmit = useCallback(async () => {
    setDisabled(true);
    schemaClient
      .validate({
        corporate_name,
        fantasy_name,
        cpf_cnpj,
        birthday,
        home_phone,
        office_phone,
        cell_phone,
        email,
        is_supplier,
        status,
        covenant: covenant_id,
        covenant_card_number,
        gender,
        naturalness,
        nationality,
        marital_status,
        profession,
        responsible,
        cpf_responsible,
        rg_responsible,
        client_code,
      })
      .then(async (formValidated: any) => {
        await patchClients(id, formValidated)
          .then(() => {
            addToast({
              type: 'info',
              title: 'Cliente alterado',
              description: 'Dados de cliente alterado com sucesso.',
            });
            setDisabled(false);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao editar.',
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
        setDisabled(false);
        return;
      });
  }, [
    addToast,
    corporate_name,
    fantasy_name,
    cpf_cnpj,
    birthday,
    home_phone,
    office_phone,
    cell_phone,
    email,
    status,
    is_supplier,
    covenant_id,
    covenant_card_number,
    gender,
    naturalness,
    nationality,
    marital_status,
    profession,
    responsible,
    cpf_responsible,
    rg_responsible,
    client_code,
  ]);

  const handleCreateSubmit = useCallback(async () => {
    setDisabled(true);
    schemaClient
      .validate({
        corporate_name,
        fantasy_name,
        cpf_cnpj,
        birthday,
        home_phone,
        office_phone,
        cell_phone,
        email,
        is_supplier,
        covenant: covenant_id,
        covenant_card_number,
        gender,
        naturalness,
        nationality,
        marital_status,
        profession,
        responsible,
        cpf_responsible,
        rg_responsible,
        client_code,
      })
      .then(async (formValidated: any) => {
        await postClient(formValidated)
          .then(() => {
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: 'Cliente cadastrado com sucesso',
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar.',
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
        setDisabled(false);
        return;
      });
  }, [
    addToast,
    corporate_name,
    fantasy_name,
    cpf_cnpj,
    birthday,
    home_phone,
    office_phone,
    cell_phone,
    email,
    is_supplier,
    covenant_id,
    covenant_card_number,
    gender,
    naturalness,
    nationality,
    marital_status,
    profession,
    responsible,
    cpf_responsible,
    rg_responsible,
    client_code,
  ]);

  useEffect(() => {
    if (id !== 'new') {
      loadData();
    }
    listCovenants();
  }, [id]);

  useEffect(() => {
    if (birthday) {
      const today = new Date();
      const birth = new Date(birthday);
      const age = today.getFullYear() - birth.getFullYear();
      age < 18 ? setShowResponsable(true) : setShowResponsable(false);
    }
  }, [birthday]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<TeamOutlined />}
        title={id === 'new' ? 'Cadastrar cliente' : `Editar: ${corporate_name}`}
        description="Gerencie dados cadastrais, contatos e informações complementares do cliente no novo padrão visual interno."
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%' }}>
            <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Row>
                <Col>
                  <Form.Item label="Fornecedor ?" style={{ marginLeft: 10 }}>
                    <ASwitch
                      checked={is_supplier}
                      checkedChildren="Sim"
                      unCheckedChildren="Não"
                      onChange={() => {
                        setIs_supplier(!is_supplier);
                      }}
                    />
                  </Form.Item>
                </Col>
                {configuration?.show_client_code && (
                  <Col>
                    <Form.Item label="Cod. Cliente" style={{ marginLeft: 10 }}>
                      <AInput
                        placeholder="Informe o Cod. Cliente"
                        value={client_code || ''}
                        type="number"
                        maxLength={18}
                        onChange={(e) => setClient_code(+e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {clientObj && (
                <Col>
                  <OptionsClient client={clientObj} size="middle" />
                </Col>
              )}
            </Row>
            <Row>
              <Col span={6}>
                <Form.Item label="CPF/CNPJ" required>
                  {id === 'new' ? (
                    <AInputSearch
                      placeholder="Informe o CPF/CNPJ"
                      value={cpf_cnpj}
                      maxLength={18}
                      onSearch={handleSearchCnpj}
                      onChange={(e) => setCpf_cnpj(mask(e.target.value))}
                      enterButton={is_supplier}
                    />
                  ) : (
                    <AInput
                      placeholder="Informe o CPF/CNPJ"
                      value={cpf_cnpj}
                      maxLength={18}
                      onChange={(e) => setCpf_cnpj(mask(e.target.value))}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={cpf_cnpj?.length > 14 ? 9 : 18}>
                <Form.Item
                  label={is_supplier ? 'Razão Social' : 'Nome'}
                  required
                >
                  <AInput
                    placeholder="Informe a razão social"
                    value={corporate_name}
                    onChange={(e) => setCorporate_name(e.target.value)}
                  />
                </Form.Item>
              </Col>
              {cpf_cnpj?.length > 14 && (
                <Col span={9}>
                  <Form.Item label="Nome Fantasia">
                    <AInput
                      placeholder="Informe o nome fantasia"
                      value={fantasy_name}
                      onChange={(e) => setFantasy_name(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Row>
              <Col span={6}>
                <Form.Item
                  label={cpf_cnpj?.length > 14 ? 'D. Cadastro' : 'Nascimento'}
                  required
                >
                  <AInput
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Celular" required>
                  <AInput
                    placeholder="Informe o celular"
                    value={cell_phone}
                    onChange={(e) =>
                      setCell_phone(cellPhoneMask(e.target.value))
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Telefone Residencial">
                  <AInput
                    placeholder="Informe o telefone residencial"
                    value={home_phone}
                    onChange={(e) => setHome_phone(phoneMask(e.target.value))}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Telefone Comercial">
                  <AInput
                    placeholder="Informe o telefone comercial"
                    value={office_phone}
                    onChange={(e) => setOffice_phone(phoneMask(e.target.value))}
                  />
                </Form.Item>
              </Col>
            </Row>
            {!is_supplier && (
              <Row>
                <Col span={4}>
                  <Form.Item label="Sexo">
                    <ASelect
                      placeholder="Sexo"
                      value={gender}
                      onChange={(e) => setGender(e)}
                      allowClear
                      options={[
                        {
                          label: 'Masculino',
                          value: 'm',
                        },
                        {
                          label: 'Feminino',
                          value: 'f',
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Estado Civil">
                    <ASelect
                      placeholder="Informe o estado civil"
                      value={marital_status}
                      onChange={(e) => setMarital_status(e)}
                      allowClear
                      options={MARITAL_STATUS_LIST.map((status) => ({
                        label: status.label,
                        value: status.value,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Nacionalidade">
                    <ASelect
                      placeholder="Informe a nacionalidade"
                      value={nationality}
                      onChange={(e) => setNationality(e)}
                      allowClear
                      options={[
                        {
                          label: 'Brasileiro(a)',
                          value: 'Brasileiro(a)',
                        },
                        {
                          label: 'Extrangeiro(a)',
                          value: 'Extrangeiro(a)',
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Naturalidade">
                    <AInput
                      placeholder="Informe a naturalidade"
                      value={naturalness}
                      onChange={(e) => setNaturalness(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Profissão">
                    <AInput
                      placeholder="Informe a profissão"
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row>
              <Col span={8}>
                <Form.Item label="E-mail">
                  <AInput
                    placeholder="Informe o e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Item>
              </Col>
              {!is_supplier && (
                <>
                  <Col span={8}>
                    <Form.Item label="Convênio">
                      <ASelect
                        showSearch
                        placeholder="Selecione o convênio"
                        optionFilterProp="children"
                        onChange={(e) => setCovenant_id(e)}
                        filterOption={(input, option: any) =>
                          (option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        value={covenant_id}
                        allowClear
                        options={covenants.map((covenant) => ({
                          label: covenant.description,
                          value: covenant.id,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Nº do cartão de convênio">
                      <AInput
                        placeholder="Informe o Nº do cartão de convênio"
                        value={covenant_card_number}
                        onChange={(e) =>
                          setCovenant_card_number(e.target.value)
                        }
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            {birthday && showResponsable && (
              <>
                <Title level={5} style={{ marginTop: 0 }}>
                  Responsável Legal
                </Title>
                <Row>
                  <Col span={8}>
                    <Form.Item label="CPF responsável">
                      <AInput
                        placeholder="Informe o cpf do responsável"
                        value={cpf_responsible}
                        onChange={(e) => setCpf_responsible(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="RG responsável">
                      <AInput
                        placeholder="Informe o rg do responsável"
                        value={rg_responsible}
                        onChange={(e) => setRg_responsible(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Nome do responsável">
                      <AInput
                        placeholder="Informe o nome do responsável"
                        value={responsible}
                        onChange={(e) => setResponsible(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {id !== 'new' && (
              <Row>
                <Col span={4}>
                  <Form.Item label="Ativo">
                    <ASwitch
                      checked={status}
                      checkedChildren="Sim"
                      unCheckedChildren="Não"
                      onChange={() => {
                        setStatus(!status);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <OptionFooter
              onClickCancel={() => navigate(-1)}
              disabled={disabled}
              onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
            />
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default ClientCreateEdit;
